import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { reports } from "../lib/api";
import { money } from "../lib/utils";
import { useActiveBranch } from "../lib/session";
import { QueryError } from "../components/QueryError";

// Reportes: report_sales (totales, por método, top productos) + report_inventory_kpis
// (valor, stock muerto, GMROI). Gráficos en CSS puro (sin lib extra).
export const Route = createFileRoute("/reportes")({ component: Reportes });

function Reportes() {
  const branch = useActiveBranch();
  const sales = useQuery({ queryKey: ["report_sales", branch], queryFn: () => reports.sales(branch) });
  const kpis = useQuery({ queryKey: ["report_inv", branch], queryFn: () => reports.inventoryKpis(branch) });

  const top = ((sales.data?.top_products ?? []) as { name: string; net_amount: number; margin: number }[]);
  const maxNet = Math.max(1, ...top.map((t) => t.net_amount));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Reportes</h1>
      <QueryError error={sales.error ?? kpis.error} />

      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card label="Ventas (mes)" value={money(sales.data?.totals?.gross_total ?? 0)} />
        <Card label="Valor inventario" value={money(kpis.data?.inventory_value ?? 0)} />
        <Card label="Stock muerto" value={money(kpis.data?.dead_stock?.value ?? 0)} sub={`${kpis.data?.dead_stock?.count ?? 0} SKUs`} accent />
        <Card label="GMROI" value={kpis.data?.gmroi_approx != null ? kpis.data.gmroi_approx.toFixed(2) : "—"} />
      </section>

      <section className="rounded-xl border bg-white p-5">
        <h2 className="mb-3 font-semibold">Top productos por venta neta</h2>
        {top.length === 0 ? <p className="text-grafito-400">Sin datos en el período.</p> : (
          <div className="space-y-2">
            {top.map((t, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-40 truncate text-sm">{t.name}</span>
                <div className="h-5 flex-1 rounded bg-grafito-100">
                  <div className="h-5 rounded bg-brand" style={{ width: `${(t.net_amount / maxNet) * 100}%` }} />
                </div>
                <span className="w-24 text-right text-sm font-medium">{money(t.net_amount)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-xl border bg-white p-5">
        <h2 className="mb-3 font-semibold">Ventas por método de pago</h2>
        <div className="flex flex-wrap gap-3 text-sm">
          {Object.entries(sales.data?.by_method ?? {}).map(([m, v]) => (
            <span key={m} className="rounded-lg bg-grafito-100 px-3 py-1.5">{m}: <b>{money(v as number)}</b></span>
          ))}
          {Object.keys(sales.data?.by_method ?? {}).length === 0 && <span className="text-grafito-400">Sin ventas.</span>}
        </div>
      </section>
    </div>
  );
}

function Card({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm">
      <div className="text-xs text-grafito-400">{label}</div>
      <div className={`mt-1 text-xl font-bold ${accent ? "text-brand" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-grafito-400">{sub}</div>}
    </div>
  );
}
